import { atom as m, map as w, computed as b } from "nanostores";
import { copilotTools as C, copilotInstructions as E, copilotContext as F, uniqueId as x } from "./global-state.js";
function S() {
  return Math.floor(Date.now() / 1e3);
}
const v = {
  idle: ["initializing", "ending"],
  initializing: ["ready", "error"],
  ready: ["sending", "ending"],
  sending: ["receiving"],
  receiving: ["ready"],
  ending: ["ended", "error"],
  error: ["initializing", "ending"],
  ended: []
  // Can only transition to itself (terminal state)
};
function I(i, e) {
  if (i === e)
    return i;
  if (!v[i])
    throw new Error(`Unknown status: ${i}`);
  if (v[i].includes(e))
    return e;
  throw new Error(`Invalid status transition: ${i} -> ${e}`);
}
class P {
  constructor(e) {
    this.config = {
      includeGlobalContext: !0,
      includeGlobalTools: !0,
      includeGlobalInstructions: !0
    }, this.__status = m("idle"), this.__messages = m([]), this.__meta = m({}), this.__uploadFiles = m([]), this.__queuedMessages = m([]), this.__abortController = null, this.__enableWebSearch = m(!0), this.__serverSideTools = w({}), this.__sessionInstructions = w({}), this.__sessionTools = w({}), this.__sessionContext = w({}), this.__tools = b(
      [this.__serverSideTools, C, this.__sessionTools],
      (t, n, a) => {
        const r = {}, s = {
          ...this.config.includeGlobalTools === !0 ? n : {},
          ...a
        };
        for (const o of Object.values(t))
          if (o != null) {
            const c = s[o.name];
            r[o.name] = {
              ...c?.type === "server" ? c : void 0,
              ...o
            };
          }
        for (const o of Object.values(s))
          o != null && o.type === "client" && r[o.name] == null && (r[o.name] = o);
        return r;
      }
    ), this.__statefulMessages = b([this.__messages, this.__tools], j), this.__instructions = b(
      [E, this.__sessionInstructions],
      (t, n) => ({
        ...this.config.includeGlobalInstructions === !0 ? t : {},
        ...n
      })
    ), this.__context = b([F, this.__sessionContext], (t, n) => ({
      ...this.config.includeGlobalContext === !0 ? t : {},
      ...n
    })), this.__suggestionsRefreshTrigger = m([]), e && (this.config = { ...this.config, ...e });
  }
  /**
   * The current status of the Copilot session.
   */
  get status() {
    return this.__status.get();
  }
  /**
   * If the session is being ended.
   */
  get isEnding() {
    return this.status === "ending";
  }
  get isEnded() {
    return this.status === "ended";
  }
  /**
   * Sets the status of the Copilot session.
   */
  setStatus(e) {
    this.__status.set(I(this.status, e));
  }
  /**
   * adds an instruction just for this session
   */
  addInstruction(e) {
    const t = x();
    return this.__sessionInstructions.setKey(t, e), () => {
      this.__sessionInstructions.setKey(t, void 0);
    };
  }
  /**
   * Adds a tool just for this session
   */
  addTool(e) {
    return this.__sessionTools.setKey(e.name, e), () => {
      this.__sessionTools.setKey(e.name, void 0);
    };
  }
  /**
   * Adds a context just for this session
   */
  addContext(e) {
    const t = x();
    return this.__sessionContext.setKey(t, e), () => {
      this.__sessionContext.setKey(t, void 0);
    };
  }
  /**
   * Sets the meta data for this session
   */
  setSessionMeta(e) {
    this.__meta.set(e);
  }
  /**
   * Gets the meta data for this session
   */
  getSessionMeta() {
    return this.__meta.get();
  }
  /**
   * Sets whether the model can search the web to answer questions.
   */
  setWebSearchEnabled(e) {
    this.__enableWebSearch.set(e);
  }
  /**
   * Gets whether web search is enabled for this session.
   */
  getWebSearchEnabled() {
    return this.__enableWebSearch.get();
  }
  /**
   * Updates the meta data for a specific message by targeting message.meta.id
   */
  setMessageMeta(e, t) {
    const a = this.__messages.get().map((r) => r.role === "model" && r.type === "meta" && "meta" in r && r.meta?.id === e ? {
      ...r,
      meta: t
    } : r);
    this.__messages.set(a);
  }
  /**
   * Clears any messages that are queued to be sent after the current response completes.
   * Returns the messages that were cleared so they can be restored if needed.
   */
  clearQueuedMessages() {
    const e = this.__queuedMessages.get();
    return this.__queuedMessages.set([]), e;
  }
  /**
   * Stops the current in-flight exchange.
   *
   * Aborts the streaming HTTP request immediately (so the UI becomes responsive
   * right away) and fires a request to cancel the Temporal workflow on the
   * backend (so it stops burning LLM tokens).
   *
   * Always returns `{ messages }` — the messages that were queued while the
   * exchange was running — so the caller can restore them to the input box
   * regardless of whether the backend call succeeded. If the backend call
   * fails, `error` is also set so the caller can surface it to the user.
   *
   * Never throws. Safe to call in any state.
   */
  async stop() {
    if (this.status !== "sending" && this.status !== "receiving")
      return { messages: [] };
    this.__abortController?.abort();
    const e = this.clearQueuedMessages(), t = window.__hydra_copilot__?.stopExchange;
    if (t != null && this.id != null)
      try {
        await t({ id: this.id });
      } catch (n) {
        return {
          messages: e,
          error: n instanceof Error ? n : new Error(String(n))
        };
      }
    return { messages: e };
  }
  /**
   * Forces a refresh of suggestions for this session.
   * This will trigger any active suggestion hooks to regenerate their suggestions.
   * @param instruction - Custom instructions to include in the next suggestions fetch.
   * These instructions will be used once and then cleared.
   */
  refreshSuggestions(e) {
    this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
  }
  /**
   * Initializes a new session, either from an existing session id,
   * or by creating a new one
   */
  async initialize(e) {
    if (this.status !== "idle" && this.status !== "error")
      return this.id;
    this.setStatus("initializing");
    try {
      const t = window.__hydra_copilot__?.startSession;
      if (t == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      if (e == null) {
        const n = await t();
        if (n.id == null)
          throw new Error("Failed to start session, no session ID returned");
        this.id = n.id, this.__serverSideTools.set(T(n.tools ?? []));
      } else {
        this.id = e;
        const n = await this.loadSession();
        this.__messages.set(n.messages ?? []), this.__meta.set(n.meta ?? {}), this.__serverSideTools.set(T(n.tools ?? []));
      }
      return this.setStatus("ready"), this.id;
    } catch (t) {
      throw this.setStatus("error"), t;
    }
  }
  /**
   * Ends the current session, clearing all messages and state.
   */
  async end() {
    this.setStatus("ending");
    try {
      const e = window.__hydra_copilot__?.endSession;
      if (e == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      this.id != null && await e(this.id), this.__messages.set([]), this.__queuedMessages.set([]), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
    } catch (e) {
      throw this.setStatus("error"), e;
    }
  }
  async sendMessage(e) {
    const t = typeof e == "string" ? [{ role: "user", type: "text", text: e, timestamp: S() }] : e, n = window.__hydra_copilot__?.sendStreamingMessage;
    if (n == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.status === "idle" && await this.initialize(), this.status !== "ready") {
      if (this.status === "sending" || this.status === "receiving") {
        this.__queuedMessages.set([...this.__queuedMessages.get(), ...t]);
        return;
      }
      throw new Error(`Cannot send message while session is in "${this.status}" state.`);
    }
    let a = null;
    try {
      this.setStatus("sending"), this.__abortController = new AbortController();
      let r = t.map((o) => ({
        ...o,
        timestamp: o.timestamp || S()
      })), u = 0;
      for (; r.length > 0; ) {
        this.__messages.set([...this.__messages.get(), ...r]);
        const o = [], c = (d) => {
          if (this.setStatus("receiving"), u += 1, this.__messages.set([...this.__messages.get(), d]), d.role === "model" && d.type == "tool_call")
            for (const h of d.tool_calls) {
              if (h.needs_approval || h.is_client_tool !== !0)
                continue;
              const p = this.__tools.get()[h.name];
              o.push(
                (async () => {
                  try {
                    if (p?.type === "server")
                      throw new Error(
                        `Tool ${h.name} is a server-side tool and cannot be called on the client.`
                      );
                    if (p?.handler == null)
                      throw new Error(`Tool ${h.name} does not have a handler defined.`);
                    const f = await p.handler({ args: h.args, session: this, toolCallId: h.id }) ?? null;
                    return {
                      tool_call_id: h.id,
                      name: h.name,
                      response: { result: f, error: null },
                      finished: !0
                    };
                  } catch (f) {
                    return {
                      tool_call_id: h.id,
                      name: h.name,
                      response: {
                        result: null,
                        error: `Error calling tool ${h.name}: ${f instanceof Error ? f.message : String(f)}`
                      },
                      finished: !0
                    };
                  }
                })()
              );
            }
          if (d.role === "user" && d.type === "tool_response")
            for (const h of d.tool_responses) {
              const p = this.__tools.get()[h.name], f = z(
                [...this.__messages.get(), ...r],
                h.tool_call_id
              );
              p?.type === "server" && p?.handler != null && h.finished === !0 && p.handler({
                args: f?.args || {},
                session: this,
                response: h.response,
                toolCallId: h.tool_call_id,
                wasApproved: h.was_approved === !0
              });
            }
        }, _ = {
          id: this.id,
          messages: r,
          tools: U(this.__tools.get()),
          instructions: q(this.__instructions.get()),
          context: R(this.__context.get()),
          onMessage: c,
          signal: this.__abortController?.signal,
          enable_web_search: this.getWebSearchEnabled()
        };
        if (await n(_), o.length === 0) {
          r = [];
          continue;
        }
        r = [
          {
            role: "user",
            type: "tool_response",
            tool_responses: await Promise.all(o),
            timestamp: S()
          }
        ];
      }
      if (u === 0)
        throw new Error("No messages received from the server. Please check your connection.");
      this.setStatus("ready");
      const s = this.__queuedMessages.get();
      if (s.length > 0) {
        this.__queuedMessages.set([]);
        const o = s.filter((l) => l.type === "text" && "text" in l), _ = [...s.filter((l) => l.type !== "text")];
        if (o.length > 0) {
          const l = o.map((g) => "text" in g ? g.text : "").join(`

`);
          _.push({
            role: "user",
            type: "text",
            text: l,
            timestamp: S()
          });
        }
        a = _;
      }
    } catch (r) {
      if (this.setStatus("receiving"), this.setStatus("ready"), A(r))
        return;
      throw r;
    } finally {
      this.__abortController = null;
    }
    a != null && this.sendMessage(a).catch(() => {
    });
  }
  /**
   * Loads the session data (messages and meta)
   */
  async loadSession() {
    const e = window.__hydra_copilot__?.getSession;
    if (e == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.id == null)
      throw new Error("Session not started");
    return await e(this.id);
  }
  /**
   * Waits for the session to be ready
   */
  async waitUntilReady() {
    if (this.status !== "ready") {
      if (this.status === "idle") {
        await this.initialize();
        return;
      }
      if (this.status === "initializing")
        return new Promise((e, t) => {
          let n = null, a = null;
          const r = () => {
            n !== null && (clearTimeout(n), n = null), a !== null && (a(), a = null);
          };
          a = this.__status.subscribe((u) => {
            u === "ready" ? (r(), e()) : u === "error" ? (r(), t(new Error("Session initialization failed"))) : u !== "initializing" && (r(), t(new Error(`Unexpected session status: ${u}`)));
          }), n = setTimeout(() => {
            r(), t(new Error("Session initialization timeout - exceeded 30 seconds"));
          }, 3e4);
        });
      throw this.status === "error" ? new Error("Session is in error state") : new Error(`Cannot wait for session in "${this.status}" state`);
    }
  }
  /**
   * Gets upload signature for file upload
   * Note: Session readiness is now checked at the upload level (uploadCopilotFile)
   */
  async getUploadSignature(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getUploadSignature");
    const t = window.__hydra_copilot__;
    if (!t?.getUploadSignature)
      throw new Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
    return await t.getUploadSignature({
      id: this.id,
      size: e.size,
      mime_type: e.mime_type
    });
  }
  /**
   * Gets download URL for a previously uploaded file
   */
  async getFileDownloadUrl(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
    const t = window.__hydra_copilot__;
    if (!t?.getFileDownloadUrl)
      throw new Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
    return (await t.getFileDownloadUrl({
      id: this.id,
      filename: e
    })).url;
  }
  /**
   * Add files to the upload queue
   */
  addUploadFiles(e) {
    const t = this.__uploadFiles.get();
    this.__uploadFiles.set([...t, ...e]);
  }
  /**
   * Update a specific upload file
   */
  updateUploadFile(e, t) {
    const a = this.__uploadFiles.get().map((r) => r.id === e ? { ...r, ...t } : r);
    this.__uploadFiles.set(a);
  }
  /**
   * Remove upload file
   */
  removeUploadFile(e) {
    const n = this.__uploadFiles.get().filter((a) => a.id !== e);
    this.__uploadFiles.set(n);
  }
  /**
   * Clear all upload files
   */
  clearUploadFiles() {
    this.__uploadFiles.set([]);
  }
}
function M(i) {
  return typeof i == "string" ? i : typeof i == "number" || typeof i == "boolean" ? String(i) : Array.isArray(i) || typeof i == "object" ? JSON.stringify(i) : String(i);
}
function j(i, e) {
  const t = [];
  let n = [];
  const a = {}, r = i.filter(
    (s) => {
      if (s.role === "user" && s.type === "tool_response") {
        for (const o of s.tool_responses)
          o.tool_call_id != null && (a[o.tool_call_id] == null && (a[o.tool_call_id] = []), a[o.tool_call_id].push(o));
        return !1;
      }
      return s.type === "text" || s.type === "content" || s.type === "tool_call" || s.type === "meta" || s.type === "error" || s.type === "compaction_start" || s.type === "compaction_complete";
    }
  );
  function u() {
    const s = n.filter((d) => d.type === "text"), o = n.find((d) => d.type === "meta" && "meta" in d);
    if (n = [], s.length === 0)
      return;
    const c = s.map((d) => d.text).join(""), _ = o?.meta ?? {}, l = s[0]?.thought;
    let g;
    if (l === !0) {
      const d = t[t.length - 1];
      d != null && d.timestamp != null ? g = Math.max(0, s[s.length - 1].timestamp - d.timestamp) : g = 0;
    }
    t.push({
      role: "model",
      type: "text",
      text: c,
      timestamp: s[0].timestamp,
      meta: _,
      ...l === !0 && { thought: l },
      ...g != null && { duration: g }
    }), n = [];
  }
  for (const s of r)
    if (s.role === "model" && s.type === "text") {
      const o = n.some((l) => l.type === "text"), c = s.thought === !0, _ = n.some(
        (l) => l.type === "text" && "thought" in l && l.thought === !0
      );
      o && (c && !_ || !c && _) && u(), n.push(s);
    } else if (s.role === "user" && (s.type === "text" || s.type === "content"))
      u(), t.push(s);
    else if (s.role === "model" && s.type === "meta")
      n.push(s), u();
    else if (s.role === "model" && s.type === "tool_call")
      u(), t.push({
        role: "model",
        type: "tool_call",
        tool_calls: s.tool_calls.map((o) => {
          const c = a[o.id], _ = e[o.name];
          if (c == null || c.length === 0)
            return {
              ...o,
              status: "executing"
            };
          const l = c.find((p) => p.response.error != null && p.response.error !== ""), g = c.some((p) => p.finished), d = c.some((p) => p.was_approved === !0);
          if (l != null)
            return {
              ...o,
              status: "error",
              response: l.response,
              ...d && { was_approved: !0 }
            };
          const h = _?.type === "server" && _?.isStreaming === !0;
          return g && c.length === 1 && !h ? {
            ...o,
            status: "success",
            response: c[0].response,
            ...d && { was_approved: !0 }
          } : {
            ...o,
            status: g ? "success" : "executing",
            response: {
              result: c.map((p) => p.response.result),
              error: null
            },
            ...d && { was_approved: !0 }
          };
        }),
        timestamp: s.timestamp
      });
    else if (s.role === "model" && s.type === "compaction_start")
      u(), t.push({
        role: "model",
        type: "compaction",
        status: "compacting",
        timestamp: s.timestamp
      });
    else if (s.role === "model" && s.type === "compaction_complete") {
      u();
      const o = t.findIndex(
        (_) => _.role === "model" && _.type === "compaction" && _.status === "compacting"
      ), c = {
        role: "model",
        type: "compaction",
        status: "complete",
        summary: s.text,
        timestamp: s.timestamp
      };
      o >= 0 ? t[o] = c : t.push(c);
    } else
      s.role === "model" && s.type === "error" ? (u(), t.push(s)) : console.error("Unknown message type", s);
  u();
  for (let s = 0; s < t.length; s++) {
    const o = t[s];
    if (o.role !== "model" || o.type !== "tool_call" || !o.tool_calls.some((l) => l.status === "executing"))
      continue;
    t.slice(s + 1).some((l) => l.role === "model" && (l.type === "text" || l.type === "tool_call" || l.type === "error")) && (t[s] = {
      ...o,
      tool_calls: o.tool_calls.map((l) => l.status === "executing" ? { ...l, status: "success" } : l)
    });
  }
  return t;
}
function U(i) {
  return Object.values(i).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
    name: e.name,
    description: typeof e.description == "string" ? y(e.description) : e.description,
    request_json_schema: e.requestSchema,
    json_response_schema: e.responseSchema
  }));
}
function q(i) {
  return Array.isArray(i) ? i.map((e) => y(e.instruction).trim()) : Object.values(i).filter((e) => e !== void 0).map((e) => y(e.instruction).trim());
}
function R(i) {
  return Array.isArray(i) ? i.map((e) => ({
    description: y(e.description),
    value: M(typeof e.value == "function" ? e.value() : e.value)
  })) : Object.values(i).filter((e) => e !== void 0).map((e) => ({
    description: y(e.description),
    value: M(typeof e.value == "function" ? e.value() : e.value)
  }));
}
function y(i) {
  const e = i.split(`
`), t = e.filter((u) => u.trim().length > 0);
  if (t.length === 0)
    return i;
  const n = t.map((u) => {
    const s = u.match(/^(\s*)/);
    return s ? s[1].length : 0;
  }), a = Math.min(...n);
  return e.map((u) => u.trim().length === 0 ? u : u.slice(a)).join(`
`);
}
function z(i, e) {
  for (const t of i)
    if (t.role === "model" && t.type === "tool_call") {
      const n = t.tool_calls.find((a) => a.id === e);
      if (n)
        return n;
    }
}
function A(i) {
  return i instanceof DOMException && i.name === "AbortError" || i != null && typeof i == "object" && "name" in i && i.name === "CanceledError";
}
function T(i) {
  const e = {};
  for (const t of i)
    e[t.name] = {
      type: "server",
      name: t.name,
      description: t.description,
      requestSchema: t.request_json_schema,
      responseSchema: t.response_json_schema
    };
  return e;
}
export {
  P as CopilotSession,
  z as findToolCallById,
  R as prepareContextForTransport,
  q as prepareInstructionsForTransport,
  U as prepareToolsForTransport,
  T as serverSideToolsToCopilotTools,
  M as stringifyForCopilot,
  y as undent,
  I as updatedStatus
};
